/* eslint-disable no-useless-escape */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";
import Button from "../Button/Button";
import ImageUpload from "../Common/ImageUpload";
import SectionTitle from "../Common/SectionTitle";
import { useAppContext } from "../../context/AppContext";
import useSubscriptionLimits from "../../hooks/useSubscriptionLimits";
import UsageLimitModal from "../UsageRestriction/UsageLimitModal";
import {
  defaultInputStyle,
  defaultInputInvalidStyle,
  defaultInputLargeStyle,
  defaultInputLargeInvalidStyle,
  defaultSkeletonLargeStyle,
  defaultSkeletonNormalStyle,
} from "../../constants/defaultStyles";
import {
  addNewClient,
  getClientNewForm,
  updateNewClientFormField,
} from "../../store/clientSlice";

const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

const emptyForm = {
  id: "",
  image: "",
  name: "",
  email: "",
  billingAddress: "",
  mobileNo: "",
  GST: "",
};

function QuickAddClient({ editForm }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clientNewForm = useSelector(getClientNewForm);
  const { initLoading: isInitLoading } = useAppContext();
  const { canCreateResource } = useSubscriptionLimits();

  const [isTouched, setIsTouched] = useState(false);
  const [clientForm, setClientForm] = useState(emptyForm);
  const [showUsageLimitModal, setShowUsageLimitModal] = useState(false);
  const [canCreateClient, setCanCreateClient] = useState(true); // Cache the permission check
  
  // Check permissions once on component mount to avoid repeated calls
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const canCreate = await canCreateResource('clients');
        setCanCreateClient(canCreate);
      } catch (error) {
        // Optimistic fallback - allow creation
        setCanCreateClient(true);
      }
    };
    
    checkPermissions();
  }, [canCreateResource]);

  const [validForm, setValidForm] = useState(
    Object.keys(emptyForm).reduce((a, b) => {
      return { ...a, [b]: false };
    }, {})
  );

  const onChangeImage = useCallback(
    (str) => {
      setClientForm((prev) => ({ ...prev, image: str }));
      dispatch(updateNewClientFormField({ key: "image", value: str }));
    },
    [dispatch]
  );

  const handlerClientValue = useCallback(
    (event, keyName) => {
      const value = event.target.value;

      setClientForm((prev) => {
        return { ...prev, [keyName]: value };
      });

      dispatch(updateNewClientFormField({ key: keyName, value }));
    },
    [dispatch]
  );

  const submitHandler = useCallback(async () => {
    setIsTouched(true);

    const isValid = Object.keys(validForm).every((key) => validForm[key]);

    if (!isValid) {
      toast.error("Invalid Client Form!", {
        position: "bottom-center",
        autoClose: 2000,
      });
      return;
    }

    try {
      // Check subscription limits before creating client (now async)
      const canCreate = await canCreateResource('clients');

      if (!canCreate) {
        console.log('FREE PLAN: Client creation blocked - limit reached');
        toast.error("You've reached your client limit! Upgrade to Pro to add more clients.", {
          position: "bottom-center",
          autoClose: 3000,
        });
        setShowUsageLimitModal(true);
        return;
      }

      console.log('FREE PLAN: Client creation allowed - adding client');
      
      toast.success("Client added successfully!", {
        position: "bottom-center",
        autoClose: 2000,
      });

      const newClientId = nanoid();
      dispatch(addNewClient({ ...clientForm, id: newClientId }));
      
      setIsTouched(false);
      
      // Refresh page to trigger sync
      setTimeout(() => {
        window.location.reload();
      }, 2500);
    } catch (error) {
      console.error('Error checking subscription limits:', error);
      toast.error("Error checking subscription limits. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
  }, [clientForm, dispatch, validForm, canCreateResource]);

  // Handler for when disabled button is clicked - redirect to subscription page
  const handleDisabledClick = useCallback(() => {
    setShowUsageLimitModal(true);
  }, []);

  const handleUpgrade = () => {
    setShowUsageLimitModal(false);
    // Navigate to subscription page
    navigate('/subscription');
  };

  const imageUploadClasses = useMemo(() => {
    const defaultStyle = "rounded-xl ";

    if (!clientForm.image) {
      return defaultStyle + " border-dashed border-2 border-indigo-400 ";
    }

    return defaultStyle;
  }, [clientForm]);

  useEffect(() => {
    const isValidEmail =
      clientForm?.email?.trim() && clientForm?.email.match(emailRegex);

    setValidForm((prev) => ({
      id: true,
      image: true,
      name: clientForm?.name?.trim() ? true : false,
      email: isValidEmail ? true : false,
      billingAddress: clientForm?.billingAddress?.trim() ? true : false,
      mobileNo: clientForm?.mobileNo?.trim() ? true : false,
      GST: true,
    }));
  }, [clientForm]);

  useEffect(() => {
    if (clientNewForm) {
      setClientForm(clientNewForm);
    }
  }, [clientNewForm]);

  return (
    <>
      <div className="bg-white rounded-xl p-4">
        <SectionTitle> Quick Add Client </SectionTitle>
        <div className="flex mt-2">
          {isInitLoading ? (
            <Skeleton className="skeleton-input-radius skeleton-image border-dashed border-2" />
          ) : (
            <ImageUpload
              keyName="QuickEditImageUpload"
              className={imageUploadClasses}
              url={clientForm.image}
              onChangeImage={onChangeImage}
            />
          )}

          <div className="flex-1 pl-3">
            {isInitLoading ? (
              <Skeleton className={defaultSkeletonLargeStyle} />
            ) : (
              <div>
                <input
                  autoComplete="nope"
                  value={clientForm.name}
                  placeholder="User Name"
                  className={
                    !validForm.name && isTouched
                      ? defaultInputLargeInvalidStyle
                      : defaultInputLargeStyle
                  }
                  onChange={(e) => handlerClientValue(e, "name")}
                  disabled={isInitLoading}
                />
              </div>
            )}
          </div>
        </div>
        
        <div className="flex mt-2">
          <div className="flex-1">
            {isInitLoading ? (
              <Skeleton className={defaultSkeletonNormalStyle} />
            ) : (
              <input
                autoComplete="nope"
                placeholder="Email Address"
                className={
                  !validForm.email && isTouched
                    ? defaultInputInvalidStyle
                    : defaultInputStyle
                }
                disabled={isInitLoading}
                value={clientForm.email}
                onChange={(e) => handlerClientValue(e, "email")}
              />
            )}
          </div>
        </div>
        <div className="flex mt-2">
          <div className="flex-1">
            {isInitLoading ? (
              <Skeleton className={defaultSkeletonNormalStyle} />
            ) : (
              <input
                autoComplete="nope"
                placeholder="Mobile No"
                className={
                  !validForm.mobileNo && isTouched
                    ? defaultInputInvalidStyle
                    : defaultInputStyle
                }
                disabled={isInitLoading}
                value={clientForm.mobileNo}
                onChange={(e) => handlerClientValue(e, "mobileNo")}
              />
            )}
          </div>
        </div>
        
        <div className="flex mt-2">
          <div className="flex-1">
            {isInitLoading ? (
              <Skeleton className={defaultSkeletonNormalStyle} />
            ) : (
              <input
                autoComplete="nope"
                placeholder="Billing Address"
                className={
                  !validForm.billingAddress && isTouched
                    ? defaultInputInvalidStyle
                    : defaultInputStyle
                }
                disabled={isInitLoading}
                value={clientForm.billingAddress}
                onChange={(e) => handlerClientValue(e, "billingAddress")}
              />
            )}
          </div>
        </div>

        <div className="flex mt-2">
          <div className="flex-1">
            {isInitLoading ? (
              <Skeleton className={defaultSkeletonNormalStyle} />
            ) : (
              <input
                autoComplete="nope"
                placeholder="GSTIN"
                className={defaultInputStyle}
                disabled={isInitLoading}
                value={clientForm.GST || ""}
                onChange={(e) => handlerClientValue(e, "GST")}
              />
            )}
          </div>
        </div>

        <div className="mt-3">
          <Button 
            onClick={canCreateClient ? submitHandler : handleDisabledClick} 
            block={1}
            disabled={!canCreateClient}
          >
            <span className="inline-block ml-2"> 
              {canCreateClient ? 'Submit' : 'Upgrade to Add Clients'} 
            </span>
          </Button>
        </div>
      </div>

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showUsageLimitModal}
        onClose={() => setShowUsageLimitModal(false)}
        onUpgrade={handleUpgrade}
        resourceType="clients"
        message="You've reached your client limit. Upgrade to add more clients and grow your business!"
      />
    </>
  );
}

export default QuickAddClient;
